import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { colors, Frame } from 'hui';
import { RcFile, UploadChangeParam, UploadProps } from 'antd/lib/upload/interface';
import { doeApiUrl } from '../../../api';
import RemindArea from './RemindArea';
import ParseArea from './ParseArea';
import RestartProcessArea from './RestartProcessArea';
import ViewAssessmentArea from './ViewAssessmentArea';
import UploadArea from './UploadArea';
import { UploadFile } from 'antd/es/upload/interface';

const UploadZone = styled.div`
  margin-top: 1rem;
  padding: 3.5rem 4rem;
  background: ${colors.tint6};
  border-radius: 0.5rem;
`;

const UploadPage: FC<{}> = () => {
  const [uploadStatus, setUploadStatus] = useState('');
  const [parseStatus, setParseStatus] = useState('');
  const [uploadButtonDisabled, setUploadButtonDisabled] = useState(false);
  const [uploadDetail, setUploadDetail] = useState<string[]>(['']);
  const [uploadFailReason, setUploadFailReason] = useState('');
  const [fileList, setFileList] = useState([]);
  const [parseResult, setParseResult] = useState('');

  const onChange = (info: UploadChangeParam<UploadFile>): void => {
    setFileList(info.fileList);
    if (info.file.status === 'uploading') {
      setUploadButtonDisabled(true);
    } else if (info.file.status === 'done') {
      setUploadStatus('success');
      setParseStatus('parsing');
      setParseResult('success');
    } else if (info.file.status === 'error') {
      if (info.file.response && info.file.response.type === 'Upload failed') {
        setUploadStatus('fail');
        setUploadDetail(info.file.response.detail);
        setUploadFailReason(info.file.response.failReason);
      }
      if (info.file.response && info.file.response.type === 'Parse failed') {
        setParseStatus('parsing');
        setUploadDetail(info.file.response.detail);
        setUploadFailReason(info.file.response.failReason);
        setParseResult('fail');
      }
    }
  };

  const closeParseProgressBar = (value: string): void => {
    setParseStatus(value);
  };

  const beforeUpload = (file: RcFile): boolean => {
    const isCSV = file.type === 'text/csv';
    if (!isCSV) {
      setUploadButtonDisabled(true);
      setUploadStatus('fail');
      setUploadFailReason('File of wrong type');
      setUploadDetail(['Only CSV files are allowed']);
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      setUploadButtonDisabled(true);
      setUploadStatus('fail');
      setUploadFailReason('File too large');
      setUploadDetail(['Only files of up to 10MB are allowed']);
    }
    return isCSV && isLt10M;
  };

  const restartProcess = (): void => {
    setUploadStatus('');
    setParseStatus('');
    setUploadButtonDisabled(false);
    setFileList([]);
  };

  const props: UploadProps = {
    accept: '.csv',
    action: `${doeApiUrl}assessments`,
    method: 'PUT',
    showUploadList: {
      showPreviewIcon: false,
      showRemoveIcon: false,
      showDownloadIcon: false,
    },
    onChange: onChange,
    beforeUpload: beforeUpload,
    fileList: fileList,
  };

  return (
    <Frame size={0}>
      <UploadZone>
        <RemindArea />

        <UploadArea
          uploadProps={props}
          fileList={fileList}
          uploadButtonDisabled={uploadButtonDisabled}
          uploadStatus={uploadStatus}
          responseDetail={uploadDetail}
          responseFailReason={uploadFailReason}
        />

        {parseStatus && (
          <ParseArea
            parseStatus={parseStatus}
            parseFinalResult={parseResult}
            responseDetail={uploadDetail}
            responseFailReason={uploadFailReason}
            closeParseProgressBar={closeParseProgressBar}
          />
        )}

        {(uploadStatus === 'fail' || parseStatus === 'fail') && (
          <RestartProcessArea restartProcess={restartProcess} />
        )}

        {parseStatus === 'success' && <ViewAssessmentArea />}
      </UploadZone>
    </Frame>
  );
};

export default UploadPage;
